import { FC } from 'react';
import { Card, withStyles, Flag, formatAmount, VirtualAccount } from 'vopay';
import { Button, CopyButton } from 'UI';
import { useTranslation } from 'react-i18next';
import { Balance } from '@/hooks/useApi/useAccountBalances';
import { AccountType } from '../AccountSummaryPage';
import AccountTypeBadge from '../AccountUI/AccountTypeBadge';
// import StartInterestTooltip from '../AccountUI/Tooltip/StartInterestTooltip';
import styles from './BalanceListItem.module.scss';
import { useClientData } from '@/providers/ClientData.provider';

const cn = withStyles(styles);

type BalanceListItemProps = {
  accountType: AccountType;
  balance: Balance;
  selected: boolean;
  virtualAccount?: VirtualAccount;
  onSimulateInboundTransfer: (virtualAccountID: string, currency: string) => void;
  onOpenModal: () => void;
};

const BalanceListItem: FC<BalanceListItemProps> = ({
  accountType,
  balance,
  selected,
  onSimulateInboundTransfer,
  virtualAccount,
  // onOpenModal
}) => {
  const simulateInboundTransferEnabled = import.meta.env.VITE_SIMULATE_INBOUND_TRANSFERS_ENABLED === 'true';
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.AccountBalances' });
  const {
    response: { VirtualAccountsEnabled },
  } = useClientData();
  const account = virtualAccount
    ? [
        virtualAccount.IBAN,
        virtualAccount.BicSwift,
        virtualAccount.FinancialInstitutionNumber,
        virtualAccount.BranchTransitNumber,
        virtualAccount.ABARoutingNumber,
        virtualAccount.AccountNumber,
      ]
        .filter((value) => Boolean(value))
        .join('-')
    : '';
  return (
    <div
      style={{
        padding: selected ? '2px' : '1px',
        borderRadius: '20px',
        background: selected
          ? 'linear-gradient(135deg, hsla(0, 0%, 72%, 0.5), hsla(0, 0%, 22%, 0.8), hsla(0, 0%, 72%, 0.5))'
          : 'transparent',
        boxShadow: selected ? undefined : '0 0 34px hsla(0, 0%, 18%, 0.10)',
      }}
    >
      <Card
        className={cn('balanceListItem')}
        style={{
          padding: '16px',
          backgroundColor: selected ? '#f0f0f0' : '#ffffff',
          boxShadow: selected ? undefined : '0 0 34px hsla(0, 0%, 18%, 0.10)',
          border: 'none',
        }}
      >
        <Flag flag={balance.Currency} width="40px" />
        <div className={cn('content')}>
          <div>
            {accountType === 'disabled' ? (
              // when the interest account is disabled, hide the functionality for now. it will be shown as a locked feature later
              // <StartInterestTooltip onOpenModal={onOpenModal} customElement={<AccountTypeBadge status={accountType} />} />
              <></>
            ) : (
              <div style={{ paddingBottom: '8px' }}>
                <AccountTypeBadge status={accountType} />
              </div>
            )}
            <div className={cn('currencyInfo')}>
              <div className={cn('currency')}>{balance.Currency}</div>
              {virtualAccount && (
                <div className={cn('accountInfo')}>
                  <span>{account}</span>
                  <CopyButton content={account} size={'16px'} />
                </div>
              )}
            </div>
            <div className={cn('grid')}>
              <div className={cn('gridLabel')}>{t('accountBalance', { defaultValue: 'Account Balance' })}</div>
              <div className={cn(balance.AccountBalance < 0 && 'negative')}>{formatAmount(balance.AccountBalance)}</div>

              <div className={cn('gridLabel')}>{t('availableBalance', { defaultValue: 'Available Balance' })}</div>
              <div className={cn(balance.AccountBalance < 0 && 'negative')}>{formatAmount(balance.AvailableFunds)}</div>
            </div>
          </div>
          {VirtualAccountsEnabled &&
            simulateInboundTransferEnabled &&
            virtualAccount &&
            virtualAccount.Status === 'Active' && (
              <Button
                onClick={() => {
                  onSimulateInboundTransfer(virtualAccount.VirtualAccountID, balance.Currency);
                }}
                iconLeftProps={{
                  icon: 'arrow_downward',
                  className: cn('simulateIcon'),
                }}
                size="small"
              ></Button>
            )}
        </div>
      </Card>
    </div>
  );
};

export default BalanceListItem;
